import { useState, useCallback } from 'react';
import Cropper, { type Area } from 'react-easy-crop';
import 'react-easy-crop/react-easy-crop.css';

interface ImageCropperProps {
  image: string;
  aspect?: number;
  onComplete: (croppedImageDataUrl: string) => void;
  onCancel: () => void;
}

function getCroppedImg(imageSrc: string, pixelCrop: Area): Promise<string> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.src = imageSrc;
    image.crossOrigin = 'anonymous';
    image.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = pixelCrop.width;
      canvas.height = pixelCrop.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Canvas context not available'));
        return;
      }
      ctx.drawImage(
        image,
        pixelCrop.x,
        pixelCrop.y,
        pixelCrop.width,
        pixelCrop.height,
        0,
        0,
        pixelCrop.width,
        pixelCrop.height
      );
      resolve(canvas.toDataURL('image/jpeg', 0.92));
    };
    image.onerror = () => reject(new Error('Image load failed'));
  });
}

export default function ImageCropper({
  image,
  aspect = 3 / 4,
  onComplete,
  onCancel,
}: ImageCropperProps) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [processing, setProcessing] = useState(false);

  const onCropComplete = useCallback(
    (_: Area, croppedPixels: Area) => {
      setCroppedAreaPixels(croppedPixels);
    },
    []
  );

  const handleConfirm = async () => {
    if (!croppedAreaPixels) return;
    setProcessing(true);
    try {
      const cropped = await getCroppedImg(image, croppedAreaPixels);
      onComplete(cropped);
    } catch {
      // fallback: use original image
      onComplete(image);
    } finally {
      setProcessing(false);
    }
  };

  const aspectLabel = aspect === 3 / 4 ? '3:4' : aspect === 1 ? '1:1' : `${aspect}:1`;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60" onClick={onCancel} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-brand-light/30">
          <div>
            <h3 className="text-sm font-semibold text-brand-deep">裁剪头像</h3>
            <p className="text-[11px] text-gray-400 mt-0.5">{aspectLabel} 比例 · 拖拽调整位置 · 滚轮缩放</p>
          </div>
          <button
            onClick={onCancel}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-300 hover:text-brand-deep hover:bg-brand-light transition-colors cursor-pointer"
          >
            <i className="ri-close-line" />
          </button>
        </div>

        {/* Cropper area */}
        <div className="relative w-full h-[400px] bg-gray-900">
          <Cropper
            image={image}
            crop={crop}
            zoom={zoom}
            aspect={aspect}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
            cropShape="rect"
            showGrid
          />
        </div>

        {/* Zoom control */}
        <div className="px-5 py-3 flex items-center gap-3 bg-brand-gray border-t border-brand-light/20">
          <i className="ri-zoom-out-line text-gray-400 text-sm" />
          <input
            type="range"
            min={1}
            max={3}
            step={0.01}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            className="flex-1 h-1 bg-brand-light rounded-full appearance-none cursor-pointer accent-brand-brown"
          />
          <i className="ri-zoom-in-line text-gray-400 text-sm" />
        </div>

        {/* Actions */}
        <div className="px-5 py-3 flex items-center justify-end gap-2 border-t border-brand-light/20">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-xs font-medium text-gray-400 hover:text-brand-deep hover:bg-brand-light rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            取消
          </button>
          <button
            onClick={handleConfirm}
            disabled={processing}
            className="px-5 py-2 text-xs font-medium bg-brand-deep text-white rounded-lg hover:bg-brand-dark disabled:opacity-60 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
          >
            {processing ? (
              <>
                <i className="ri-loader-4-line animate-spin mr-1" />
                处理中...
              </>
            ) : (
              <>
                <i className="ri-check-line mr-1" />
                确认裁剪
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}